/** @jsx isolateComponent */
import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  Animation,
  Colors,
  Icon,
  TooltipWrapper,
  useTheme,
} from '@zapier/design-system';

type Props = {
  /*
   * `aria-label` for the `IconButton` if `label` isn't supplied.
   */
  ariaLabel?: string;
  /*
   * `aria-hidden` for the `IconButton`.
   */
  ariaHidden?: boolean;
  /*
   * Indicates whether or not `IconButton` is disabled.
   */
  disabled?: boolean;
  /**
   * `id` of the `input` that this component points to.
   */
  id?: string;
  /**
   * Indicates whether the user is currently searching.
   */
  isSearching: boolean;
  /*
   * Label for the `IconButton`.
   */
  label?: string;
};

const Styles = labelStyles('SearchToggler', {
  root: (isRebrand: boolean) => css`
    width: 40px;
    height: 40px;
    border-radius: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${isRebrand ? Colors.neutral600 : Colors.gray60};
    transition: ${Animation.transitionValue};

    &:hover,
    &:focus {
      background-color: ${isRebrand ? 'transparent' : Colors.gray10};
      color: ${isRebrand ? Colors.neutral800 : Colors.gray60};
    }
  `,
});

/**
 * @private
 * Button that controls whether or not the search form is displayed.
 */
const SearchToggler = (props: Props) => {
  const { isRebrand } = useTheme();
  return (
    <TooltipWrapper
      content={props.label}
      position="south"
      shouldCorrectPosition={false}
      showTooltipOnTouchDevices={false}
    >
      {() => (
        <label
          aria-hidden={props.ariaHidden}
          aria-label={props.ariaLabel}
          css={Styles.root(isRebrand)}
          htmlFor={props.id}
        >
          <Icon
            name={props.isSearching ? 'formX' : 'magnifyingGlass'}
            size={24}
          />
        </label>
      )}
    </TooltipWrapper>
  );
};

export default SearchToggler;
